import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authApi, User } from '@/rtk/api/authApi';

// Types
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  lastAuthCheck: number | null;
  // Change password UI state (kept for backward compatibility)
  changePasswordLoading: boolean;
  changePasswordError: string | null;
  changePasswordSuccess: string | null;
}

// Normalize user data from API response
function normalizeUserData(input: any): User {
  return {
    id: input?.id || input?._id || 'unknown',
    name: input?.name || input?.username || '',
    email: input?.email || '',
    role: (input?.type === 'admin' || input?.role === 'admin') ? 'admin' : 'user',
    avatar: input?.avatar || input?.profileImage || undefined,
    avatar_url: input?.avatar_url || undefined,
    createdAt: input?.created_at || input?.createdAt || new Date().toISOString(),
    updatedAt: input?.updated_at || input?.updatedAt || new Date().toISOString(),
    date_of_birth: input?.date_of_birth || undefined,
    phone_number: input?.phone_number || undefined,
    whatsapp_number: input?.whatsapp_number || undefined,
    gender: input?.gender || undefined,
    address: input?.address || undefined,
    type: input?.type || undefined,
  };
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  lastAuthCheck: null,
  changePasswordLoading: false,
  changePasswordError: null,
  changePasswordSuccess: null,
};

// Auth slice - simplified, only handles local state
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isInitialized = true;
      state.lastAuthCheck = null;
    },
    initializeFromToken: (state, action: PayloadAction<string>) => {
      const basicUser = {
        id: 'temp-user',
        name: 'User',
        email: 'user@example.com',
        role: 'user' as const,
        type: 'user'
      };
      state.user = normalizeUserData(basicUser);
      state.isAuthenticated = true;
      state.isInitialized = true;
      state.lastAuthCheck = Date.now();
    },
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    clearChangePasswordState: (state) => {
      state.changePasswordLoading = false;
      state.changePasswordError = null;
      state.changePasswordSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Only sync user data from checkAuth (main source of truth)
      .addMatcher(
        authApi.endpoints.checkAuth.matchFulfilled,
        (state, action) => {
          let userData = null;
          if (action.payload.data) {
            userData = action.payload.data;
          } else if (action.payload.user) {
            userData = action.payload.user;
          } else if (action.payload) {
            userData = action.payload;
          }

          if (userData) {
            state.user = normalizeUserData(userData);
            state.isAuthenticated = true;
          } else {
            state.user = null;
            state.isAuthenticated = false;
          }

          state.isInitialized = true;
          state.lastAuthCheck = Date.now();
        }
      )
      .addMatcher(
        authApi.endpoints.checkAuth.matchRejected,
        (state) => {
          state.isInitialized = true;
        }
      )
      // Sync user from login response
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, action) => {
          const response = action.payload;

          if (response?.type) {
            const userData = {
              id: 'temp-id',
              name: 'User',
              email: 'user@example.com',
              role: response.type === 'admin' ? 'admin' : 'user',
              type: response.type
            };
            state.user = normalizeUserData(userData);
            state.isAuthenticated = true;
          } else if (response?.user) {
            state.user = normalizeUserData(response.user);
            state.isAuthenticated = true;
          }

          state.isInitialized = true;
          state.lastAuthCheck = Date.now();
        }
      )
      // Clear on logout
      .addMatcher(
        authApi.endpoints.logout.matchFulfilled,
        (state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.isInitialized = true;
          state.lastAuthCheck = null;
        }
      )
      // Change password cases (kept for backward compatibility)
      .addMatcher(
        authApi.endpoints.changePassword.matchPending,
        (state) => {
          state.changePasswordLoading = true;
          state.changePasswordError = null;
          state.changePasswordSuccess = null;
        }
      )
      .addMatcher(
        authApi.endpoints.changePassword.matchFulfilled,
        (state, action) => {
          state.changePasswordLoading = false;
          const response = action.payload;
          if (response.success) {
            state.changePasswordSuccess = response.message || 'Password updated successfully';
            state.changePasswordError = null;
          } else {
            state.changePasswordError = response.message || 'Failed to change password';
            state.changePasswordSuccess = null;
          }
        }
      )
      .addMatcher(
        authApi.endpoints.changePassword.matchRejected,
        (state, action) => {
          state.changePasswordLoading = false;
          state.changePasswordSuccess = null;
          const errorMessage = action.payload?.data?.message || action.error?.message || 'Failed to change password';
          state.changePasswordError = errorMessage;
        }
      );
  },
});

export const { clearAuth, initializeFromToken, updateUserProfile, clearChangePasswordState } = authSlice.actions;
export default authSlice.reducer;
